import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { orderColumnKey, type OrderListItem } from "@school-sales-ai/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  MAX_ORDERS_PER_COLUMN,
  ORDERS_PER_COLUMN,
  useMoveOrder,
  useOrderColumnOverflow,
} from "@/hooks/useOrders";
import {
  groupOrdersByColumn,
  moveTargetsFor,
  resolveOrderDrop,
  type OrderBoardColumn,
} from "@/lib/order-board";
import { useOrdersStore } from "@/stores/orders.store";
import { OrderCardBody } from "./OrderCardBody";
import { OrderKanbanColumn } from "./OrderKanbanColumn";
import { OrderMoveDialog } from "./OrderMoveDialog";

type PendingMove = { order: OrderListItem; kind: "deliver" | "cancel" };

type Props = {
  /** Every board column, in board order. */
  columns: OrderBoardColumn[];
  /** The orders the board request brought: the first page of each column. */
  items: OrderListItem[];
  /** True per-column totals from the server. */
  counts: Record<string, number>;
  isLoading: boolean;
};

/**
 * The order a drag carries. dnd-kit types `data` as an open record; the only
 * writer is `OrderKanbanCard`, which puts the order there, so the cast states
 * what that component guarantees rather than guessing.
 */
function draggedOrder(data: Record<string, unknown> | undefined): OrderListItem | null {
  return (data?.order as OrderListItem | undefined) ?? null;
}

/**
 * One paginated desktop column. Its own component because the overflow query is
 * a hook and cannot run in a loop inside the board.
 */
function BoardColumn({
  column,
  pageOrders,
  count,
  activeId,
  accepts,
}: {
  column: OrderBoardColumn;
  pageOrders: OrderListItem[];
  count: number;
  activeId: string | null;
  accepts?: boolean;
}): React.JSX.Element {
  const limit = useOrdersStore((s) => s.columnLimits[column.key]);
  const growColumn = useOrdersStore((s) => s.growColumn);
  const setColumns = useOrdersStore((s) => s.setColumns);
  const setViewMode = useOrdersStore((s) => s.setViewMode);
  const overflow = useOrderColumnOverflow(column.key, limit);
  // The overflow page is re-filtered to this column: after an optimistic move
  // the cached page can still hold a card that just left.
  const loaded = (overflow ?? pageOrders).filter((o) => orderColumnKey(o) === column.key);
  const canLoadMore = (limit ?? ORDERS_PER_COLUMN) < MAX_ORDERS_PER_COLUMN;

  return (
    <OrderKanbanColumn
      column={column}
      orders={loaded}
      count={count}
      activeId={activeId}
      accepts={accepts}
      onLoadMore={canLoadMore ? () => growColumn(column.key, ORDERS_PER_COLUMN) : undefined}
      loadMoreCount={Math.min(ORDERS_PER_COLUMN, count - loaded.length)}
      onSeeAll={() => {
        setColumns([column.key]);
        setViewMode("table");
      }}
    />
  );
}

/** A tappable card on the phone board, with the moves it allows in a menu. */
function MobileOrderCard({
  order,
  columns,
  onMove,
}: {
  order: OrderListItem;
  columns: OrderBoardColumn[];
  onMove: (order: OrderListItem, column: OrderBoardColumn) => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  // Built by the same rule a drop is resolved by, so the menu can never offer a
  // move the board would refuse.
  const targets = moveTargetsFor(order, columns);

  return (
    <div className="flex flex-col gap-1.5 rounded-[14px] border border-ink-100 bg-white p-[14px] shadow-sm">
      <button
        type="button"
        onClick={() => navigate(`/pedidos/${order.id}`)}
        className="flex flex-col gap-1.5 text-left"
      >
        <OrderCardBody order={order} />
      </button>
      {targets.length > 0 && (
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex cursor-pointer items-center gap-1 rounded-[6px] bg-lime-pale px-2 py-1 text-[11px] font-semibold text-moss"
              >
                <ArrowRightLeft className="h-3 w-3" aria-hidden />
                Mover a…
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs text-ink-500">Mover a</DropdownMenuLabel>
              {targets.map((c) => (
                <DropdownMenuItem key={c.key} onSelect={() => onMove(order, c)} className="gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${c.swatchClass}`} aria-hidden />
                  {c.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

/**
 * The Kanban half of /pedidos.
 *
 * `columns` always spans every column — the column filter narrows what is
 * RENDERED, never what is fetched, so the counts «Columnas» trusts to block
 * deleting a non-empty column stay honest (`BoardView` does the same for leads).
 */
export function OrderBoardView({ columns, items, counts, isLoading }: Props): React.JSX.Element {
  const moveOrder = useMoveOrder();
  const columnFilter = useOrdersStore((s) => s.columns);
  const growColumn = useOrdersStore((s) => s.growColumn);

  const visibleColumns =
    columnFilter.length > 0 ? columns.filter((c) => columnFilter.includes(c.key)) : columns;
  const grouped = groupOrdersByColumn(items, columns);

  const [activeOrder, setActiveOrder] = useState<OrderListItem | null>(null);
  const [pending, setPending] = useState<PendingMove | null>(null);
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);

  const visibleKeys = visibleColumns.map((c) => c.key);
  // The phone opens on "Por entregar" when it is shown — it is the work queue,
  // and an open cart is nothing staff can act on yet.
  const defaultTab = visibleKeys.includes("confirmed") ? "confirmed" : (visibleKeys[0] ?? null);
  const activeTab =
    activeTabKey && visibleKeys.includes(activeTabKey) ? activeTabKey : defaultTab;
  const activeTabColumn = visibleColumns.find((c) => c.key === activeTab) ?? null;

  // The phone shows one column at a time and reads the same cache entry the
  // desktop column does, so this costs no extra request.
  const mobileLimit = useOrdersStore((s) => (activeTab ? s.columnLimits[activeTab] : undefined));
  const mobileOverflow = useOrderColumnOverflow(activeTab ?? "", mobileLimit);
  const mobileOrders = (mobileOverflow ?? (activeTab ? (grouped[activeTab] ?? []) : [])).filter(
    (o) => orderColumnKey(o) === activeTab,
  );
  const mobileCount = activeTab ? (counts[activeTab] ?? mobileOrders.length) : 0;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function move(order: OrderListItem, column: OrderBoardColumn): void {
    const action = resolveOrderDrop(order, column);
    if (action.type === "no-op") return;
    if (action.type === "refused") {
      toast.error(action.reason);
      return;
    }
    if (action.type === "deliver" || action.type === "cancel") {
      setPending({ order, kind: action.type });
      return;
    }
    moveOrder.mutate({ order, action });
  }

  function handleDragStart({ active }: DragStartEvent): void {
    setActiveOrder(draggedOrder(active.data.current));
  }

  function handleDragEnd({ active, over }: DragEndEvent): void {
    const order = draggedOrder(active.data.current);
    setActiveOrder(null);
    if (!order || !over) return;
    const column = columns.find((c) => c.key === String(over.id));
    if (column) move(order, column);
  }

  function confirmPending(reason?: string): void {
    if (!pending) return;
    moveOrder.mutate(
      { order: pending.order, action: { type: pending.kind }, reason },
      { onSettled: () => setPending(null) },
    );
  }

  return (
    <>
      {/* ── Mobile ────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col md:hidden">
        <div className="flex-shrink-0 overflow-x-auto border-b border-ink-100 bg-white [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-0.5 px-2">
            {visibleColumns.map((column) => {
              const isActive = column.key === activeTab;
              return (
                <button
                  key={column.key}
                  type="button"
                  onClick={() => setActiveTabKey(column.key)}
                  className={`flex flex-shrink-0 cursor-pointer flex-col items-center gap-[3px] border-b-2 px-3 py-2.5 transition-all ${
                    isActive ? "border-moss" : "border-transparent"
                  }`}
                >
                  <span
                    className={`whitespace-nowrap text-[11px] ${
                      isActive ? "font-semibold text-moss" : "font-normal text-ink-600"
                    }`}
                  >
                    {column.label}
                  </span>
                  <span
                    className={`rounded-full px-[7px] py-[1px] text-[10px] font-semibold ${
                      isActive ? "bg-lime text-moss" : "bg-ink-100 text-ink-600"
                    }`}
                  >
                    {counts[column.key] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 py-3">
          {isLoading &&
            [1, 2, 3].map((i) => (
              <div key={i} className="h-[96px] animate-pulse rounded-[14px] bg-ink-100" />
            ))}
          {!isLoading && activeTabColumn && mobileOrders.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-12">
              <span className="text-3xl opacity-25">📦</span>
              <span className="font-display text-[18px] font-bold text-ink-400">
                Sin pedidos en esta columna
              </span>
            </div>
          )}
          {!isLoading &&
            mobileOrders.map((order) => (
              <MobileOrderCard key={order.id} order={order} columns={columns} onMove={move} />
            ))}
          {!isLoading && activeTab && mobileOrders.length < mobileCount && (
            <div className="flex flex-col items-center gap-1.5 py-3">
              <span className="text-[11px] text-ink-400">
                {mobileOrders.length} de {mobileCount}
              </span>
              {(mobileLimit ?? ORDERS_PER_COLUMN) < MAX_ORDERS_PER_COLUMN && (
                <button
                  type="button"
                  onClick={() => growColumn(activeTab, ORDERS_PER_COLUMN)}
                  className="cursor-pointer rounded-md border border-ink-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-ink-700"
                >
                  Ver {Math.min(ORDERS_PER_COLUMN, mobileCount - mobileOrders.length)} más
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Desktop ───────────────────────────────── */}
      <div className="hidden min-h-0 flex-1 flex-col md:flex">
        {isLoading ? (
          <div className="flex gap-3 overflow-x-auto pb-4">
            {[0, 1, 2, 3, 4].map((col) => (
              <div key={col} className="flex w-64 flex-shrink-0 flex-col gap-2">
                <div className="h-4 w-24 animate-pulse rounded bg-ink-100" />
                <div className="min-h-[120px] rounded-xl bg-ink-050 p-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="mb-2 h-20 animate-pulse rounded-lg bg-ink-100" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-4">
              {visibleColumns.map((column) => {
                const action = activeOrder ? resolveOrderDrop(activeOrder, column) : null;
                return (
                  <BoardColumn
                    key={column.key}
                    column={column}
                    pageOrders={grouped[column.key] ?? []}
                    count={counts[column.key] ?? (grouped[column.key] ?? []).length}
                    activeId={activeOrder?.id ?? null}
                    accepts={action ? action.type !== "refused" : undefined}
                  />
                );
              })}
            </div>

            <DragOverlay>
              {activeOrder ? (
                <div className="flex w-64 rotate-1 flex-col gap-1.5 rounded-lg border border-ink-100 bg-white p-3 opacity-90 shadow-lg">
                  <OrderCardBody order={activeOrder} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {pending && (
        <OrderMoveDialog
          order={pending.order}
          kind={pending.kind}
          busy={moveOrder.isPending}
          onConfirm={confirmPending}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  );
}
